import useStripeCheckout, {
    CheckoutStripeProps,
} from 'Hooks/useStripeCheckout';
import Bell from '_assets/img/icons/svg/bell.svg';
import Coins from '_assets/img/icons/svg/coins.svg';
import StarYellow from '_assets/img/icons/svg/star_yellow.svg';
import Button from '_atoms/Button/Button';
import { TRIAL_TIMELINE_CONTENT } from '_constants/questionTreeConstants';
import i18n from '_i18n';
import useUser from '_services/Redux/User/Selector/useUser';
import React, { useEffect, useMemo, useState } from 'react';
import './TrialScreen.scss';
import ReferralContextService from '_services/Referral/ReferralContextService';
import CloseButton from 'Components/CloseButton';
import { termsAndConditionsUrl } from 'Helper/Terms';
import useNavigate from 'Hooks/useNavigate';

interface TrialScreenProps {
    onAnswer: (answerIndexes: number[]) => void;
}

const TrialScreen: React.FC<TrialScreenProps> = ({ onAnswer }) => {
    const { user } = useUser();
    const language: LanguageCode = i18n.language === 'no' ? 'no' : 'en';
    const [isLoading, setIsLoading] = useState(false);
    const isReferral = ReferralContextService.isReferred();
    const navigate = useNavigate();

    const header = {
        no: 'Slik fungerer prøveperioden',
        en: 'How the free trial works',
    };

    const trialTimelineContent = TRIAL_TIMELINE_CONTENT(
        isReferral ? 26 : 10,
        isReferral ? 30 : 14,
    );

    /** Stripe config for referral subscription */
    const checkoutSuccessUrl = useMemo(() => {
        const successUrl = new URL('/onboarding/offer', window.location.origin);
        successUrl.searchParams.set('checkoutSuccess', 'true');
        return successUrl.toString();
    }, []);
    const checkoutParams = useMemo<CheckoutStripeProps>(
        () => ({
            interval: 'year',
            success_url: checkoutSuccessUrl,
            trial: true,
            trialDuration: 30,
        }),
        [checkoutSuccessUrl],
    );
    const checkoutCacheKey = useMemo(() => `onboarding-offer-referral`, []);
    const { prefetchCheckoutUrl, onCheckoutButtonClickWithPrefetch } =
        useStripeCheckout();

    useEffect(() => {
        if (!user || !isReferral) {
            return;
        }
        prefetchCheckoutUrl(checkoutParams, {
            cacheKey: checkoutCacheKey,
            delayMs: 1000,
        });
    }, [
        checkoutCacheKey,
        checkoutParams,
        prefetchCheckoutUrl,
        user,
        isReferral,
    ]);

    const handleStripeReferralSubscription = () => {
        void onCheckoutButtonClickWithPrefetch({
            ...checkoutParams,
            cacheKey: checkoutCacheKey,
            isLoading: setIsLoading,
        });
    };

    const clearReferralContext = () => {
        ReferralContextService.clear();
    };

    return (
        <div className="trial-screen">
            <div className="content-container">
                <CloseButton
                    onClose={() => {
                        clearReferralContext();
                        navigate('/');
                    }}
                />
                <div className="trial-header">{header[language]}</div>
                <div className="trial-timeline">
                    <div className="timeline-item">
                        <div className="timeline-icon star">
                            <img src={StarYellow} alt="star" />
                        </div>
                        <div className="timeline-content">
                            <div className="timeline-day">
                                {trialTimelineContent.day1[language]}
                            </div>
                            <div
                                className="timeline-text"
                                dangerouslySetInnerHTML={{
                                    __html: trialTimelineContent.day1Text[
                                        language
                                    ],
                                }}
                            ></div>
                        </div>
                    </div>
                    <div className="timeline-dots">
                        <div className="timeline-dot"></div>
                        <div className="timeline-dot"></div>
                        <div className="timeline-dot"></div>
                        <div className="timeline-dot"></div>
                    </div>
                    <div className="timeline-item">
                        <div className="timeline-icon bell">
                            <img src={Bell} alt="bell" />
                        </div>
                        <div className="timeline-content">
                            <div className="timeline-day">
                                {trialTimelineContent.dayReminder[language]}
                            </div>
                            <div
                                className="timeline-text"
                                dangerouslySetInnerHTML={{
                                    __html: trialTimelineContent
                                        .dayReminderText[language],
                                }}
                            ></div>
                        </div>
                    </div>
                    <div className="timeline-dots">
                        <div className="timeline-dot"></div>
                        <div className="timeline-dot"></div>
                        <div className="timeline-dot"></div>
                        <div className="timeline-dot"></div>
                    </div>
                    <div className="timeline-item">
                        <div className="timeline-icon medal">
                            <img src={Coins} alt="coins" />
                        </div>
                        <div className="timeline-content">
                            <div className="timeline-day">
                                {trialTimelineContent.dayPayment[language]}
                            </div>
                            <div
                                className="timeline-text"
                                dangerouslySetInnerHTML={{
                                    __html: trialTimelineContent.dayPaymentText[
                                        language
                                    ],
                                }}
                            ></div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="navigation-container">
                {isReferral ? (
                    <>
                        <div
                            onClick={() =>
                                window.open(
                                    termsAndConditionsUrl,
                                    '_blank',
                                    'noopener,noreferrer',
                                )
                            }
                            className="referral-conditions"
                            dangerouslySetInnerHTML={{
                                __html: trialTimelineContent.conditions[
                                    language
                                ],
                            }}
                        ></div>
                        <Button
                            isLoading={isLoading}
                            classes="next-button"
                            onClick={() => handleStripeReferralSubscription()}
                        >
                            {trialTimelineContent.startTrialButton[language]}
                        </Button>
                    </>
                ) : (
                    <Button
                        classes="next-button"
                        isLoading={isLoading}
                        onClick={() => onAnswer([])}
                    >
                        {trialTimelineContent.continueButton[language]}
                    </Button>
                )}
            </div>
        </div>
    );
};

export default TrialScreen;
